import React from "react";
import { MapPin, Calendar, Search, Clock, User, Globe } from "lucide-react";
import { decodeSearchParams } from "../utils/urlParams";
import {
  formatRegionalTime,
  formatDisplayDate,
  getCountryName,
} from "./Form/formUtils";
import { useFormContext } from "../context/FormContext";

const SearchSummaryBar = ({ onModifyClick }) => {
  const {
    pickup,
    dropoff,
    pickupDate,
    dropoffDate,
    pickupTime,
    dropoffTime,
    sameLocation,
    driverCountry,
    driverAge,
  } = useFormContext();

  const getRentalDays = () => {
    if (!pickupDate || !dropoffDate) return null;

    const pickupDateTime = new Date(`${pickupDate}T${pickupTime || "00:00"}`);
    const dropoffDateTime = new Date(`${dropoffDate}T${dropoffTime || "00:00"}`);

    const diffMs = dropoffDateTime - pickupDateTime;
    if (diffMs <= 0) return null;

    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
    return `${diffDays}-day rental`;
  };

  const rentalDays = getRentalDays();
  const hasDropoff = !sameLocation && dropoff && dropoff !== pickup;

  const shortenLocation = (name) => {
    if (!name) return "—";
    return name
      .replace(", Canada", "")
      .replace(", United States", "")
      .replace(", United Kingdom", "")
      .replace(", Australia", "");
  };

  return (
    <div className="bg-white sticky top-16 z-40 py-3">
      <div className="max-w-screen-xl mx-auto px-4">
        <div className="border border-surface-border rounded-2xl shadow-sm overflow-hidden">
          {/* ── DESKTOP ── */}
          <div className="hidden lg:block">
            <div className="flex items-center divide-x divide-surface-border py-3 w-full px-2">
              {/* Location(s) */}
              <div className="flex items-center gap-3 pr-5 min-w-0 pl-2 flex-1">
                <div className="p-2 bg-primary/10 rounded-full shrink-0 mt-2">
                  <MapPin className="w-4 h-4 text-primary" />
                </div>
                {!hasDropoff ? (
                  <div className="flex flex-col min-w-0">
                    <span className="text-[10px] uppercase tracking-widest font-heading font-semibold text-text-muted leading-tight">
                      Pickup & Drop-off
                    </span>
                    <span className="text-sm font-semibold text-dark font-body whitespace-nowrap overflow-hidden text-ellipsis">
                      {shortenLocation(pickup)}
                    </span>
                  </div>
                ) : (
                  <div className="flex items-start gap-3 min-w-0">
                    <div className="flex flex-col">
                      <span className="text-[10px] uppercase tracking-widest font-heading font-semibold text-text-muted leading-tight">
                        Pick-up
                      </span>
                      <span className="text-sm font-semibold text-dark font-body">
                        {shortenLocation(pickup)}
                      </span>
                    </div>
                    <span className="text-text-muted shrink-0 mt-4">→</span>
                    <div className="flex flex-col">
                      <span className="text-[10px] uppercase tracking-widest font-heading font-semibold text-text-muted leading-tight">
                        Drop-off
                      </span>
                      <span className="text-sm font-semibold text-dark font-body">
                        {shortenLocation(dropoff)}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Pickup date/time */}
              <div className="flex items-center gap-2 px-5 shrink-0 ml-auto">
                <div className="p-2 bg-primary/10 rounded-full shrink-0 mt-3">
                  <Calendar className="w-4 h-4 text-primary" />
                </div>
                <div className="flex flex-col">
                  <span className="text-[10px] uppercase tracking-widest font-heading font-semibold text-text-muted leading-tight">
                    Pick-up
                  </span>
                  <span className="text-sm font-semibold text-dark font-body whitespace-nowrap">
                    {formatDisplayDate(pickupDate)}
                    <span className="text-text-muted font-normal">
                      {" "}
                      · {formatRegionalTime(pickupTime, driverCountry)}
                    </span>
                  </span>
                </div>
              </div>

              {/* Dropoff date/time */}
              <div className="flex items-center gap-2 px-5 shrink-0 ml-12">
                <div className="p-2 bg-primary/10 rounded-full shrink-0 mt-3">
                  <Calendar className="w-4 h-4 text-primary" />
                </div>
                <div className="flex flex-col">
                  <span className="text-[10px] uppercase tracking-widest font-heading font-semibold text-text-muted leading-tight">
                    Drop-off
                  </span>
                  <span className="text-sm font-semibold text-dark font-body whitespace-nowrap">
                    {formatDisplayDate(dropoffDate)}
                    <span className="text-text-muted font-normal">
                      {" "}
                      · {formatRegionalTime(dropoffTime, driverCountry)}
                    </span>
                  </span>
                </div>
              </div>

              {/* Modify button */}
              <div className="flex items-center pl-5 ml-auto shrink-0">
                <button
                  onClick={onModifyClick}
                  className="flex items-center gap-2 border border-primary text-primary text-sm font-semibold font-body px-4 py-2 rounded-xl hover:border-primary hover:text-primary transition-colors"
                >
                  <Search className="w-4 h-4" />
                  Modify search
                </button>
              </div>
            </div>

            {/* Pills row */}
            <div className="flex items-center gap-3 py-2.5 text-xs font-body text-text-muted pl-6 border-t border-surface-border">
              {rentalDays && (
                <div className="flex items-center gap-1.5">
                  <Clock className="w-3.5 h-3.5 text-primary shrink-0" />
                  <span className="font-semibold text-dark">{rentalDays}</span>
                </div>
              )}
              {driverAge && (
                <>
                  <span className="select-none">·</span>
                  <div className="flex items-center gap-1.5">
                    <User className="w-3.5 h-3.5 shrink-0" />
                    <span>Driver Age: {driverAge}</span>
                  </div>
                </>
              )}
              {driverCountry && (
                <>
                  <span className="select-none">·</span>
                  <div className="flex items-center gap-1.5">
                    <Globe className="w-3.5 h-3.5 shrink-0" />
                    <span>Residency: {getCountryName(driverCountry)}</span>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* ── MOBILE ── */}
          <div className="lg:hidden py-3 space-y-2 px-3">
            {/* Row 1: Location(s) */}
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <div className="p-1 bg-primary/10 rounded-full shrink-0">
                  <MapPin className="w-3.5 h-3.5 text-primary" />
                </div>
                <div className="flex flex-col min-w-0">
                  <span className="text-[9px] uppercase tracking-wide font-heading font-semibold text-text-muted">
                    {hasDropoff ? "Pick-up" : "Pickup & Drop-off"}
                  </span>
                  <span className="text-sm font-semibold text-dark font-body">
                    {shortenLocation(pickup)}
                  </span>
                </div>
              </div>
              {hasDropoff && (
                <div className="flex items-center gap-2">
                  <div className="p-1 bg-primary/10 rounded-full shrink-0">
                    <MapPin className="w-3.5 h-3.5 text-primary" />
                  </div>
                  <div className="flex flex-col min-w-0">
                    <span className="text-[9px] uppercase tracking-wide font-heading font-semibold text-text-muted">
                      Drop-off
                    </span>
                    <span className="text-sm font-semibold text-dark font-body">
                      {shortenLocation(dropoff)}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Row 2: Dates side by side */}
            <div className="flex items-center gap-2 border-t border-surface-border pt-2 overflow-x-auto">
              <div className="flex items-center gap-1.5 flex-1">
                <div className="p-1 bg-primary/10 rounded-full shrink-0">
                  <Calendar className="w-3.5 h-3.5 text-primary" />
                </div>
                <div className="flex flex-col">
                  <span className="text-[9px] uppercase tracking-wide font-heading font-semibold text-text-muted">
                    Pick-up
                  </span>
                  <span className="text-[11px] font-semibold text-dark font-body">
                    {formatDisplayDate(pickupDate)}
                  </span>
                  <span className="text-[10px] text-text-muted font-normal font-body">
                    {formatRegionalTime(pickupTime, driverCountry)}
                  </span>
                </div>
              </div>
              <div className="w-px h-8 bg-surface-border shrink-0" />
              <div className="flex items-center gap-1.5 flex-1">
                <div className="p-1 bg-primary/10 rounded-full shrink-0">
                  <Calendar className="w-3.5 h-3.5 text-primary" />
                </div>
                <div className="flex flex-col">
                  <span className="text-[9px] uppercase tracking-wide font-heading font-semibold text-text-muted">
                    Drop-off
                  </span>
                  <span className="text-[11px] font-semibold text-dark font-body">
                    {formatDisplayDate(dropoffDate)}
                  </span>
                  <span className="text-[10px] text-text-muted font-normal font-body">
                    {formatRegionalTime(dropoffTime, driverCountry)}
                  </span>
                </div>
              </div>
            </div>

            {/* Row 3: Pills + Modify */}
            <div className="flex items-center justify-between border-t border-surface-border pt-2">
              <div className="flex items-center gap-2 text-xs text-text-muted font-body flex-wrap">
                {rentalDays && (
                  <div className="flex items-center gap-1.5">
                    <div className="p-1 bg-primary/10 rounded-full shrink-0">
                      <Clock className="w-3 h-3 text-primary" />
                    </div>
                    <span className="font-semibold text-dark">
                      {rentalDays}
                    </span>
                  </div>
                )}
                {driverAge && <span>Age: {driverAge}</span>}
              </div>
              <button
                onClick={onModifyClick}
                className="flex items-center gap-1.5 border border-primary text-primary text-xs font-semibold font-body px-5 py-1.5 rounded-xl hover:border-primary hover:text-primary transition-colors shrink-0"
              >
                <Search className="w-3.5 h-3.5" />
                Modify
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SearchSummaryBar;
